import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getModelBySlug, keszHazakModels, formatPrice, formatArea } from '../data/keszHazakModels';
import MortgageCard from '../components/MortgageCard';
import LeadForm from '../components/LeadForm';
import StickyCTA from '../components/StickyCTA';
import Lightbox from '../components/Lightbox';
import ModelCard from '../components/ModelCard';

const KeszHazModel = () => {
  const { slug } = useParams();
  const [model, setModel] = useState(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadFormType, setLeadFormType] = useState('quote');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [relatedModels, setRelatedModels] = useState([]);

  useEffect(() => {
    const foundModel = getModelBySlug(slug);
    setModel(foundModel);

    if (foundModel) {
      // Kapcsolódó modellek (hasonló árú vagy típusú)
      const related = keszHazakModels
        .filter(m => m.slug !== slug)
        .sort((a, b) => Math.abs(a.price_from_eur - foundModel.price_from_eur) - Math.abs(b.price_from_eur - foundModel.price_from_eur))
        .slice(0, 3);
      setRelatedModels(related);
    }
  }, [slug]);

  if (!model) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🏠</div>
          <h1 className="text-2xl font-bold text-white mb-4">Modell nem található</h1>
          <p className="text-gray-400 mb-6">A keresett modell nem létezik vagy nem elérhető.</p>
          <Link
            to="/kesz-hazak"
            className="bg-[#e31937] hover:bg-[#b40000] text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Vissza a modellekhez
          </Link>
        </div>
      </div>
    );
  }

  const openLightbox = (index = 0) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % model.hero_images.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + model.hero_images.length) % model.hero_images.length);
  };

  const openLeadForm = (type = 'quote') => {
    setLeadFormType(type);
    setShowLeadForm(true);
  };

  const heroImages = model.hero_images.map(img => `/images/kesz-hazak/${img}`);

  return (
    <>
      <Helmet>
        <title>{model.seo.meta_title}</title>
        <meta name="description" content={model.seo.meta_description} />
        <meta property="og:title" content={model.seo.meta_title} />
        <meta property="og:description" content={model.seo.meta_description} />
        <meta property="og:image" content={`/images/kesz-hazak/${model.seo.og_image}`} />
        <meta property="og:type" content="product" />
        <link rel="canonical" href={`https://hopphomes.hu/kesz-hazak/${model.slug}`} />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": model.title,
            "description": model.short_tagline,
            "image": heroImages,
            "offers": {
              "@type": "Offer",
              "price": model.price_from_eur,
              "priceCurrency": "EUR",
              "availability": "https://schema.org/InStock"
            },
            "brand": {
              "@type": "Brand",
              "name": "HoppHomes"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Breadcrumb */}
        <div className="bg-black/20 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <nav className="flex items-center space-x-2 text-sm">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                Főoldal
              </Link>
              <span className="text-gray-600">/</span>
              <Link to="/kesz-hazak" className="text-gray-400 hover:text-white transition-colors">
                Kész Házak
              </Link>
              <span className="text-gray-600">/</span>
              <span className="text-white">{model.title}</span>
            </nav>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Hero galéria */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Fő kép */}
            <div className="space-y-4">
              <div 
                className="aspect-video rounded-xl overflow-hidden cursor-pointer group"
                onClick={() => openLightbox(0)}
              >
                <img
                  src={heroImages[0]}
                  alt={`${model.title} homlokzati nézet`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = '/images/placeholder-house.jpg';
                  }}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 text-white px-4 py-2 rounded-lg">
                    Galéria megtekintése
                  </div>
                </div>
              </div>

              {/* Thumbnail képek */}
              {model.hero_images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {heroImages.slice(1, 5).map((image, index) => (
                    <div
                      key={index}
                      className="aspect-video rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => openLightbox(index + 1)}
                    >
                      <img
                        src={image}
                        alt={`${model.title} ${index + 2}. kép`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/images/placeholder-house.jpg';
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Videó gomb */}
              {model.cta_secondary && (
                <button className="w-full bg-gray-800 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  <span>{model.cta_secondary}</span>
                </button>
              )}
            </div>

            {/* Modell információk */}
            <div className="space-y-6">
              {/* Címsor */}
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <h1 className="text-3xl md:text-4xl font-bold text-white">
                    {model.title}
                  </h1>
                  {model.status_badge && (
                    <span className="bg-green-500 text-white text-sm font-semibold px-3 py-1 rounded-full">
                      {model.status_badge}
                    </span>
                  )}
                </div>
                <p className="text-xl text-gray-300 mb-6">{model.short_tagline}</p>
              </div>

              {/* Ár és terület */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#161616] backdrop-blur-xl rounded-xl border border-white/[0.08] p-6 text-center">
                  <div className="text-2xl font-bold text-[#e31937] mb-2">
                    {formatPrice(model.price_from_eur)}
                  </div>
                  <div className="text-sm text-gray-300">ár tól</div>
                </div>
                <div className="bg-[#161616] backdrop-blur-xl rounded-xl border border-white/[0.08] p-6 text-center">
                  <div className="text-2xl font-bold text-white mb-2">
                    {formatArea(model.area_m2)}
                  </div>
                  <div className="text-sm text-gray-300">alapterület</div>
                </div>
              </div>

              {/* Highlights */}
              <div className="bg-[#161616] backdrop-blur-xl rounded-xl border border-white/[0.08] p-6">
                <h3 className="text-lg font-bold text-white mb-4">Főbb jellemzők</h3>
                <ul className="space-y-2">
                  {model.highlights.map((highlight, index) => (
                    <li key={index} className="flex items-center text-gray-300">
                      <svg className="w-4 h-4 mr-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {highlight}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA gombok */}
              <div className="space-y-3">
                <button
                  onClick={() => openLeadForm('quote')}
                  className="w-full bg-[#e31937] hover:bg-[#b40000] text-white font-semibold py-4 px-6 rounded-lg transition-colors text-lg"
                >
                  {model.cta_primary}
                </button>
                <button
                  onClick={() => openLeadForm('callback')}
                  className="w-full border border-white/30 hover:bg-white/10 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Kérek visszahívást
                </button>
              </div>
            </div>
          </div>

          {/* Leírás */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2">
              <div className="bg-[#161616] backdrop-blur-xl rounded-xl border border-white/[0.08] p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Részletes leírás</h2>
                <div className="space-y-4 text-gray-300 leading-relaxed">
                  {model.description.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
                
                {model.note && (
                  <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-200 text-sm">
                      <strong>Megjegyzés:</strong> {model.note}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Hitelkalkuláció */}
            <div>
              <MortgageCard model={model} />
            </div>
          </div>

          {/* Alaprajz */}
          <div className="mb-8">
            <div className="bg-[#161616] backdrop-blur-xl rounded-xl border border-white/[0.08] p-8">
              <h2 className="text-2xl font-bold text-white mb-6">Alaprajz</h2>
              <div 
                className="bg-white rounded-lg p-4 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => openLightbox(0)} // Itt lehetne alaprajz specifikus lightbox
              >
                <img
                  src={`/images/kesz-hazak/${model.floorplan_image}`}
                  alt={`${model.title} alaprajz`}
                  className="w-full max-w-2xl mx-auto"
                  onError={(e) => {
                    e.target.src = '/images/placeholder-floorplan.jpg';
                  }}
                />
                <p className="text-center text-gray-600 mt-4 text-sm">
                  Kattintson a nagyobb méretű megtekintéshez
                </p>
              </div>
            </div>
          </div>

          {/* Kapcsolódó modellek */}
          {relatedModels.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-8 text-center">
                Hasonló modellek
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedModels.map((relatedModel) => (
                  <ModelCard key={relatedModel.slug} model={relatedModel} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sticky CTA mobil */}
        <StickyCTA
          model={model}
          onRequestQuote={() => openLeadForm('quote')}
          onRequestCallback={() => openLeadForm('callback')}
        />

        {/* Lead form modal */}
        {showLeadForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowLeadForm(false)} />
            <div className="relative z-10 w-full max-w-lg">
              <LeadForm
                model={model}
                formType={leadFormType}
                onSuccess={() => {
                  setShowLeadForm(false);
                  alert('Köszönjük! Üzenetét sikeresen elküldtük. Hamarosan felvesszük Önnel a kapcsolatot.');
                }}
                onClose={() => setShowLeadForm(false)}
              />
            </div>
          </div>
        )}

        {/* Lightbox */}
        <Lightbox
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          images={heroImages}
          currentIndex={lightboxIndex}
          onNext={nextImage}
          onPrev={prevImage}
          title={model.title}
        />
      </div>
    </>
  );
};

export default KeszHazModel;
